import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Map as MapIcon, Locate, Layers } from "lucide-react";
import { T } from "../theme";

const API = process.env.REACT_APP_API_URL || "";


const JOURNEY_COLORS = [
  "#ff3cac", "#00f5ff", "#ffd700", "#b44fff",
  "#39ff14", "#ff6b6b", "#54a0ff", "#ff9ff3",
  "#00cec9", "#fd79a8", "#e17055", "#6c5ce7",
];

function FitBounds({ journeys }) {
  const map = useMap();
  useEffect(() => {
    const allPts = journeys.flatMap(j => j.points.map(p => [p.lat, p.lng]));
    if (allPts.length > 0) {
      map.fitBounds(allPts, { padding: [40, 40], maxZoom: 14 });
    }
  }, [journeys, map]);
  return null;
}

function JourneyLegend({ journeys, selected, onSelect }) {
  return (
    <div className="map-legend" style={{
      position: "absolute", top: 14, right: 14, zIndex: 1000,
      background: "rgba(10,5,21,0.92)", backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      border: T.border, borderRadius: 16, padding: "14px 16px",
      maxWidth: 220, maxHeight: "60vh", overflowY: "auto",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <Layers size={13} color={T.purple} />
        <p style={{
          margin: 0, fontSize: "0.7rem", fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.1em", color: T.textMuted,
        }}>
          Bracelets
        </p>
      </div>
      {journeys.map((j, i) => {
        const color = JOURNEY_COLORS[i % JOURNEY_COLORS.length];
        const active = selected === null || selected === i;
        return (
          <button
            key={j.kandiId}
            onClick={() => onSelect(selected === i ? null : i)}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              width: "100%", padding: "7px 8px", border: "none", borderRadius: 8,
              background: selected === i ? "rgba(255,255,255,0.08)" : "transparent",
              cursor: "pointer", opacity: active ? 1 : 0.35,
              transition: "all 0.2s",
            }}
          >
            <div style={{
              width: 10, height: 10, borderRadius: "50%",
              background: color, boxShadow: `0 0 8px ${color}`,
              flexShrink: 0,
            }} />
            <span style={{
              color: T.textPrimary, fontSize: "0.8rem", fontWeight: 600,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {j.emoji} {j.name}
            </span>
            <span style={{ color: T.textMuted, fontSize: "0.68rem", marginLeft: "auto", flexShrink: 0 }}>
              {j.points.length}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function MapPage() {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [journeys, setJourneys] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${API}/api/user/${user.id}/map?viewerId=${user.id}`)
      .then(res => setJourneys(res.data.journeys || []))
      .catch(err => console.error("Map data error:", err))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const visible = useMemo(() => {
    if (selected === null) return journeys;
    return [journeys[selected]];
  }, [journeys, selected]);

  const defaultCenter = [39.8283, -98.5795];
  const hasData = journeys.length > 0;

  if (!user) return null;

  return (
    <div className="map-page" style={{
      background: T.bg, color: T.textPrimary,
      fontFamily: "'Inter', system-ui, sans-serif",
      display: "flex", flexDirection: "column",
      height: "100dvh",
    }}>
      {/* Header */}
      <div className="map-header" style={{ padding: "80px 16px 12px", maxWidth: 900, margin: "0 auto", width: "100%", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <MapIcon size={20} color={T.purple} style={{ filter: `drop-shadow(0 0 8px ${T.purple})` }} />
          <h1 style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: "1.5rem", fontWeight: 700,
            color: T.pink,
            textShadow: `0 0 20px ${T.purple}60`,
          }}>
            Journey Map
          </h1>
        </div>
        <p style={{ margin: 0, color: T.textMuted, fontSize: "0.83rem" }}>
          Every kandi tells a story — see where yours have traveled
        </p>
      </div>

      {/* Map — flex: 1 fills all remaining space above the bottom nav */}
      <div className="map-container" style={{
        margin: "0 16px 20px", borderRadius: 24, overflow: "hidden",
        border: T.border, position: "relative",
        flex: 1, minHeight: 0,
        maxWidth: 900, marginLeft: "auto", marginRight: "auto", width: "calc(100% - 32px)",
      }}>
        {loading ? (
          <div style={{
            height: "100%", display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(10,5,21,0.95)",
          }}>
            <p style={{ color: T.textMuted }}>Loading map data...</p>
          </div>
        ) : !hasData ? (
          <div style={{
            height: "100%", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            background: "rgba(10,5,21,0.95)", padding: 20,
          }}>
            <Locate size={48} color="rgba(255,255,255,0.1)" style={{ marginBottom: 16 }} />
            <p style={{ color: T.textMuted, textAlign: "center", fontSize: "0.9rem", maxWidth: 320 }}>
              No location data yet. Scan kandi with location enabled to start building your journey map!
            </p>
          </div>
        ) : (
          <>
            <MapContainer
              center={defaultCenter}
              zoom={4}
              style={{ height: "100%", width: "100%", background: "#0a0515" }}
              zoomControl={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />

              <FitBounds journeys={visible} />

              {visible.map((journey, ji) => {
                const realIndex = selected !== null ? selected : ji;
                const color = JOURNEY_COLORS[realIndex % JOURNEY_COLORS.length];
                const positions = journey.points.map(p => [p.lat, p.lng]);

                return (
                  <React.Fragment key={journey.kandiId}>
                    {/* Journey line */}
                    {positions.length > 1 && (
                      <Polyline
                        positions={positions}
                        pathOptions={{
                          color, weight: 3, opacity: 0.7,
                          dashArray: "8 6",
                        }}
                      />
                    )}

                    {/* Check-in points */}
                    {journey.points.map((pt, pi) => (
                      <CircleMarker
                        key={`${journey.kandiId}-${pi}`}
                        center={[pt.lat, pt.lng]}
                        radius={pi === journey.points.length - 1 ? 8 : 5}
                        pathOptions={{
                          color,
                          fillColor: color,
                          fillOpacity: pi === journey.points.length - 1 ? 1 : 0.6,
                          weight: pi === journey.points.length - 1 ? 3 : 1,
                        }}
                      >
                        <Popup>
                          <div style={{ fontFamily: "'Inter', sans-serif", minWidth: 160 }}>
                            <strong style={{ fontSize: "0.9rem" }}>
                              {journey.emoji} {journey.name}
                            </strong>
                            <br />
                            <span style={{ fontSize: "0.8rem", color: "#666" }}>
                              {pt.location}
                            </span>
                            <br />
                            <span style={{ fontSize: "0.75rem", color: "#999" }}>
                              {pt.actionType} by {pt.username}
                            </span>
                            <br />
                            <span style={{ fontSize: "0.72rem", color: "#aaa" }}>
                              {new Date(pt.date).toLocaleDateString("en-US", {
                                month: "short", day: "numeric", year: "numeric",
                                hour: "numeric", minute: "2-digit",
                              })}
                            </span>
                          </div>
                        </Popup>
                      </CircleMarker>
                    ))}
                  </React.Fragment>
                );
              })}
            </MapContainer>

            {journeys.length > 0 && (
              <JourneyLegend journeys={journeys} selected={selected} onSelect={setSelected} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
